import React, { useMemo, useState, useRef, useEffect, forwardRef } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { ReactComponent as CircleIcon } from '../../assets/circle-outlined.svg';
import { ReactComponent as TriangleIcon } from '../../assets/triangle-outlined.svg';
import { getColor, radius, space, fontSize } from '../../style/utils';
import { isElementsColliding } from '../../utils/common';
import {
  formatTimeValue,
  localizeDate,
  timeConsts,
} from '../../utils/datetime';
import { FlexCenter, FlexColumn } from '../Flex';
import { ColorToken } from '../../style/types';
import {
  LegendWrapper,
  LegendItem,
  Divider,
  timestampFormat,
  Status,
  XPos,
  YPos,
  poleColorMap,
  statusColorMap,
  Wrapper,
  TimestampLabel,
  Chart,
  ChartBar,
  Pole,
  PoleWrapper,
  Area,
  EndPole,
  ChartEdgeBoundary,
} from './FreshnessChartCommon';
import Alert from './TestResultsAlert';

const WARN_AFTER_RANGE_INVALID_MESSAGE =
  "'error_after' threshold is smaller than 'warn_after' threshold. Ignoring 'warn_after' threshold.";
const minBarSize = 10;

const timeUnitCharMap = {
  second: 'S',
  minute: 'M',
  hour: 'H',
  day: 'D',
};

type Unit = 'minute' | 'hour' | 'day';
type Range = { count: number; period: Unit };

const convertToMinutes = (val: number, unit: Unit) => val * timeConsts[unit];

const valueLabelBackColorMap: Record<string, ColorToken> = {
  pass: 'status.success.fillStrong',
  warn: 'status.warning.fillStrong',
  fail: 'status.error.fillSoft',
  error: 'status.error.fillSoft',
  no_data: 'status.neutral.fill',
  skipped: 'status.neutral.fill',
};

const ChartValueLabelWrapper = styled(FlexCenter)<{
  $pos: XPos;
}>`
  position: absolute;
  height: 100%;
  ${({ $pos }) => ($pos === 'right' ? 'left: 0' : 'right: 0')};
  ${({ $pos }) =>
    $pos === 'right' ? 'padding-left: 12px' : 'padding-right: 12px'};
`;

const ChartValueLabelChip = styled(FlexCenter)<{
  $pos: XPos;
  $color: Status;
}>`
  border-radius: ${radius('lg')};
  padding: ${space('xxs', 'smMinus')};
  top: calc(50% - 10px);
  background: ${({ $color, $pos, theme }) =>
    $pos === 'right'
      ? theme.palette.surface.base
      : getColor(theme, valueLabelBackColorMap[$color])};
`;

const ChartValueLabel = styled(Typography)<{ $color: Status }>`
  font-size: ${fontSize('sm')};
  color: ${({ $color, theme }) => getColor(theme, statusColorMap[$color])};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  white-space: nowrap;
  text-shadow: -1px -1px 0 white, 0 -1px 0 white, 1px -1px 0 white,
    1px 0 0 white, 1px 1px 0 white, 0 1px 0 white, -1px 1px 0 white,
    -1px 0 0 white;
`;

const PoleLabel = styled(Typography)<{
  $color: 'warn' | 'fail';
}>`
  position: absolute;
  top: 0;
  font-size: ${fontSize('xxs')};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ $color, theme }) => getColor(theme, poleColorMap[$color])};
  border: 2px solid
    ${({ $color, theme }) => getColor(theme, poleColorMap[$color])};
  padding-left: ${space('smMinus')};
  padding-right: ${space('smMinus')};
  border-radius: ${radius('sm', 'sm', 'sm', 0)};
`;

const RangePole = forwardRef(
  (
    {
      color,
      extraMarginTop,
      range,
    }: {
      color: 'warn' | 'fail';
      extraMarginTop: number;
      range: Range;
    },
    ref: any,
  ) => (
    <FlexColumn
      sx={{
        justifyContent: 'flex-end',
        mt: `-${24 + extraMarginTop}px`,
        position: 'relative',
      }}
    >
      <PoleLabel $color={color} ref={ref}>
        {`${range.count}${timeUnitCharMap[range.period]}`}
      </PoleLabel>
      <Pole $color={color} $hasLabel />
    </FlexColumn>
  ),
);

const CircleIconStyled = styled(CircleIcon)`
  position: absolute;
  top: -9px;
  left: -5px;
`;

const StartPole = ({
  right,
  timestamp,
  maxLoadedAtTimeAgoInSec,
  status,
  valueLabelPos,
  timestampLabelPos,
  valueLabelRef,
  timestampLabelRef,
  startPoleRef,
}: {
  right: number;
  timestamp: string;
  maxLoadedAtTimeAgoInSec: number;
  status: Status;
  valueLabelPos: XPos;
  timestampLabelPos: XPos;
  valueLabelRef: any;
  timestampLabelRef: any;
  startPoleRef: any;
}) => (
  <PoleWrapper $right={right} ref={startPoleRef}>
    <CircleIconStyled />
    <Pole $color="start" />
    <ChartValueLabelWrapper ref={valueLabelRef} $pos={valueLabelPos}>
      <ChartValueLabelChip $pos={valueLabelPos} $color={status}>
        <ChartValueLabel $color={status}>
          {formatTimeValue(maxLoadedAtTimeAgoInSec)}
        </ChartValueLabel>
      </ChartValueLabelChip>
    </ChartValueLabelWrapper>

    <TimestampLabel
      ref={timestampLabelRef}
      sx={{
        transform: timestampLabelPos === 'center' ? 'translateX(-50%)' : '',
        right: timestampLabelPos === 'left' ? 0 : '',
        left: timestampLabelPos === 'right' ? 0 : '',
      }}
      $color="start"
      $pos="bottom"
    >
      {localizeDate(timestamp).format(timestampFormat)}
    </TimestampLabel>
  </PoleWrapper>
);

type Props = {
  lastTimestamp: string;
  maxLoadedAtTimeAgoInSec: number;
  loadedAtField?: string;
  status: Status;
  warnAfterRange: Range | { period: null; count: null };
  errorAfterRange: Range | { period: null; count: null };
};

const FreshnessChart = ({
  lastTimestamp,
  maxLoadedAtTimeAgoInSec,
  loadedAtField,
  status,
  warnAfterRange,
  errorAfterRange,
}: Props) => {
  const [animationEnded, setAnimationEnded] = useState(false);
  const [chartBarWidth, setChartBarWidth] = useState<number>();
  const [valueLabelPos, setValueLabelPos] = useState<XPos>('right');
  const [execTimeLabelPos, setExecTimeLabelPos] = useState<YPos>('bottom');
  const [timestampLabelPos, setTimestampLabelPos] = useState<XPos>('center');
  const [warnPoleExtraMarginTop, setWarnPoleExtraMarginTop] =
    useState<number>(0);
  const [failPoleExtraMarginTop, setFailPoleExtraMarginTop] =
    useState<number>(0);

  const chartBarRef = useRef<HTMLDivElement>(null);
  const endPoleRef = useRef();
  const valueLabelRef = useRef();
  const timestampLabelRef = useRef();
  const execTimeLabelRef = useRef();
  const edgeBoundaryRef = useRef(null);
  const failPoleRef = useRef(null);
  const warnPoleRef = useRef(null);
  const startPoleRef = useRef(null);

  const isWarnAfterRangeValid = useMemo(() => {
    if (warnAfterRange.period != null && errorAfterRange.period != null) {
      const warnAfterInMinutes = convertToMinutes(
        warnAfterRange.count,
        warnAfterRange.period,
      );
      const errorAfterInMinutes = convertToMinutes(
        errorAfterRange.count,
        errorAfterRange.period,
      );

      if (warnAfterInMinutes > errorAfterInMinutes) {
        return false;
      }
    }
    return true;
  }, [warnAfterRange, errorAfterRange]);

  const warnAfter = useMemo(() => {
    if (warnAfterRange.period == null) {
      return null;
    }

    if (!isWarnAfterRangeValid) return null;

    return warnAfterRange;
  }, [warnAfterRange, isWarnAfterRangeValid]);

  const errorAfter = useMemo(() => {
    if (errorAfterRange.period == null) {
      return null;
    }
    return errorAfterRange;
  }, [errorAfterRange]);

  useEffect(() => {
    if (animationEnded) {
      setChartBarWidth(chartBarRef.current?.clientWidth);

      const isValueLabelColliding = isElementsColliding(
        valueLabelRef,
        endPoleRef,
      );
      const isTimestampCollidingExec = isElementsColliding(
        timestampLabelRef,
        execTimeLabelRef,
      );
      const isTimestampCollidingBoundary = isElementsColliding(
        timestampLabelRef,
        edgeBoundaryRef,
      );

      const isWarnStartColliding = isElementsColliding(
        startPoleRef,
        warnPoleRef,
        3,
      );

      const isFailStartColliding = isElementsColliding(
        startPoleRef,
        failPoleRef,
        3,
      );

      setValueLabelPos(isValueLabelColliding ? 'left' : 'right');
      setExecTimeLabelPos(isTimestampCollidingExec ? 'top' : 'bottom');
      setTimestampLabelPos(
        isTimestampCollidingExec
          ? 'left'
          : isTimestampCollidingBoundary
          ? 'right'
          : 'center',
      );
      setWarnPoleExtraMarginTop(isWarnStartColliding ? 6 : 0);
      setFailPoleExtraMarginTop(isFailStartColliding ? 6 : 0);
    }
  }, [animationEnded]);

  const warnAfterMinutes = useMemo(
    () => warnAfter && convertToMinutes(warnAfter.count, warnAfter.period),
    [warnAfter],
  );

  const errorAfterMinutes = useMemo(
    () => errorAfter && convertToMinutes(errorAfter.count, errorAfter.period),
    [errorAfter],
  );

  const execTime = useMemo(
    () => localizeDate(lastTimestamp).add(maxLoadedAtTimeAgoInSec, 'seconds'),
    [lastTimestamp, maxLoadedAtTimeAgoInSec],
  );

  const diffInMinutes = execTime.diff(lastTimestamp, 'minutes', true);

  const sectionRanges = useMemo(() => {
    const passedSectionTime = warnAfterMinutes || errorAfterMinutes || 0;

    const warnSectionTime =
      errorAfterMinutes && warnAfterMinutes
        ? errorAfterMinutes - warnAfterMinutes
        : 0;

    const maxSectionTime = Math.max(passedSectionTime, warnSectionTime);

    const pass = {
      min: 0,
      max: warnAfterMinutes || errorAfterMinutes || 0,
    };

    const warn = warnAfterMinutes
      ? {
          min: warnAfterMinutes,
          max: errorAfterMinutes || warnAfterMinutes + maxSectionTime,
        }
      : undefined;

    const fail = errorAfterMinutes
      ? {
          min: errorAfterMinutes,
          max: errorAfterMinutes + maxSectionTime,
        }
      : undefined;

    return { pass, warn, fail };
  }, [errorAfterMinutes, warnAfterMinutes]);

  const chartPercValue = useMemo(() => {
    const curStatusRange = sectionRanges[status];

    if (!curStatusRange) {
      return 0;
    }

    const curStatusTotalTime = curStatusRange
      ? curStatusRange.max - curStatusRange.min
      : 0;

    const sectionCount = warnAfter && errorAfter ? 3 : 2;
    const sectionIndex =
      status === 'pass' ? 0 : status === 'warn' ? 1 : sectionCount - 1;

    const graphMax = curStatusTotalTime * sectionCount;
    const chartValFixed =
      diffInMinutes - curStatusRange.min + curStatusTotalTime * sectionIndex;
    const percValue = Math.min((chartValFixed / graphMax) * 100, 100);
    const minPercValue = chartBarWidth ? (minBarSize / chartBarWidth) * 100 : 0;

    return Math.max(minPercValue, percValue);
  }, [
    sectionRanges,
    status,
    warnAfter,
    errorAfter,
    diffInMinutes,
    chartBarWidth,
  ]);

  return (
    <Wrapper>
      <LegendWrapper>
        <LegendItem>
          <CircleIcon />
          <Typography>
            Latest timestamp ({loadedAtField || 'N/A'} column)
          </Typography>
        </LegendItem>
        <Divider />
        <LegendItem>
          <TriangleIcon />
          <Typography>Test exe. time</Typography>
        </LegendItem>
      </LegendWrapper>

      <Chart>
        <ChartBar ref={chartBarRef}>
          <ChartEdgeBoundary ref={edgeBoundaryRef} />
          {errorAfter && (
            <RangePole
              color="fail"
              range={errorAfter}
              ref={failPoleRef}
              extraMarginTop={failPoleExtraMarginTop}
            />
          )}
          {warnAfter && (
            <RangePole
              color="warn"
              range={warnAfter}
              ref={warnPoleRef}
              extraMarginTop={warnPoleExtraMarginTop}
            />
          )}
          {animationEnded && (
            <StartPole
              right={chartPercValue}
              timestamp={lastTimestamp}
              maxLoadedAtTimeAgoInSec={maxLoadedAtTimeAgoInSec}
              status={status}
              valueLabelPos={valueLabelPos}
              timestampLabelPos={timestampLabelPos}
              valueLabelRef={valueLabelRef}
              timestampLabelRef={timestampLabelRef}
              startPoleRef={startPoleRef}
            />
          )}
          <Area
            onAnimationEnd={() => setAnimationEnded(true)}
            $color={status}
            $width={chartPercValue}
          />
          <EndPole
            timestamp={execTime}
            showTimestamp={animationEnded}
            ref={endPoleRef}
            execTimeLabelRef={execTimeLabelRef}
            execTimeLabelPos={execTimeLabelPos}
          />
        </ChartBar>
      </Chart>
      {!isWarnAfterRangeValid && (
        <Alert severity="warning">{WARN_AFTER_RANGE_INVALID_MESSAGE}</Alert>
      )}
    </Wrapper>
  );
};

export default React.memo(FreshnessChart);
